import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass, Home, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Catch-all for URLs that don't match any route. Before this existed,
 * unmatched paths rendered the app shell with an empty <main>, which read
 * as a broken page — and auto-generated breadcrumb segments could strand
 * users there with no way out.
 */
export default function NotFoundPage() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    const canGoBack = idx > 0;

    return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="rounded-full bg-secondary p-4 mb-5">
                <Compass className="h-8 w-8 text-foreground/60" />
            </div>
            <h1 className="text-2xl font-black tracking-tight text-shell-foreground">Page not found</h1>
            <p className="text-sm text-shell-foreground/80 mt-2 max-w-md">
                {/* #825 m3: the chip inherited the shell's light text on a light
                    bg-secondary — the path rendered invisible ("There's nothing
                    at ▯▯▯▯"). Explicit foreground so the one clue is readable. */}
                There's nothing at{" "}
                <code className="font-mono text-xs bg-secondary text-secondary-foreground rounded px-1.5 py-0.5">
                    {pathname}
                </code>
                . It may have moved, or the link is out of date.
            </p>
            <div className="flex items-center gap-3 mt-6">
                {canGoBack && (
                    <Button variant="outline" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4 mr-1.5" /> Go back
                    </Button>
                )}
                <Button asChild>
                    <Link to="/">
                        <Home className="h-4 w-4 mr-1.5" /> Home
                    </Link>
                </Button>
            </div>
        </div>
    );
}
